<template>
  <div v-bind="$attrs" :class="['ews-checkbox ews-checkbox-outer', outerClass, $attrs?.class || ''].join(' ')">
    <label v-if="label" :class="labelClass">{{ label }}</label>
    <div :class="['ews-checkbox-wrapper', wrapperClass].join(' ')">
      <!-- Multiple checkboxes with options -->
      <div v-if="normalizedOptions && normalizedOptions.length > 0" :class="['ews-checkbox-element', elementClass].join(' ')">
        <div v-for="option in normalizedOptions" :key="option.value" class="checkbox-option">
          <slot name="antDesignSlot" :option="option" :antDesignProps="antDesignProps">
            <form-antd-checkbox
              v-bind="antDesignProps || {}"
              :disabled="option.disabled"
              :value="option.value"
              :modelValue="isChecked(option.value)"
              @change="toggleOption(option.value)"
            />
          </slot>
          <label :for="option.id" class="checkbox-label">{{ option.label }}</label>
        </div>
      </div>
      <!-- Single checkbox -->
      <div v-else :class="['ews-checkbox-element', elementClass].join(' ')">
        <slot name="antDesignSlot" :antDesignProps="antDesignProps">
          <form-antd-checkbox
            v-bind="antDesignProps || {}"
            :modelValue="modelValue"
            @update:value="(newValue) => $emit('update:modelValue', newValue)"
            @update:modelValue="(newValue) => $emit('update:modelValue', newValue)"
          />
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import FormAntdCheckbox from './AntDesign/FormAntdCheckbox.vue'

export default {
  name: 'FormCheckbox',
  inheritAttrs: false,
  components: {
    FormAntdCheckbox
  },
  props: {
    /**
     * Type of box can either be 'checkbox' or 'radio'.
     */
    type: {
      type: String,
      default: 'checkbox'
    },
    /**
     * Optional label element above text input.
     */
    label: {
      type: String,
      default: ''
    },
    /**
     * Default (current) value.
     * @model
     */
    modelValue: [String, Number, Object, Array, Boolean],
    /**
     * This can be an array of values, array of objects or an object.
     */
    options: [Object, Array],
    /**
     * Additional classes for the wrapper.
     */
    wrapperClass: {
      type: String,
      default: ''
    },
    /**
     * Additional classes for the outer.
     */
    outerClass: {
      type: String,
      default: ''
    },
    /**
     * Additional classes for the inner.
     */
    elementClass: {
      type: String,
      default: ''
    },
    /**
     * Additional classes for the input.
     */
    inputClass: {
      type: String,
      default: ''
    },
    /**
     * Additional classes for the label.
     */
    labelClass: {
      type: String,
      default: ''
    },
    /**
     * Additional classes for help text.
     */
    helpClass: {
      type: String,
      default: 'text-cool-gray-500 font-light text-sm ml-1'
    },
    /**
     * Additional classes for error text.
     */
    errorClass: {
      type: String,
      default: 'text-red-700 font-light text-sm ml-1'
    },
    /**
     * Ant Design Props.
     */
    antDesignProps: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  emits: ['update:modelValue'],
  computed: {
    normalizedOptions() {
      if (!this.options) return []

      if (Array.isArray(this.options)) {
        return this.options.map((item, i) => {
          if (typeof item === 'string' || typeof item === 'number') {
            return {
              value: item.toString(),
              label: item.toString(),
              id: `checkbox-${item.toString().trim()}-${i}`,
              disabled: false
            }
          }
          return {
            ...item,
            id: item.id || `checkbox-${item.value?.toString().trim()}-${i}`,
            disabled: item.disabled || false
          }
        })
      }

      if (typeof this.options === 'object') {
        return Object.entries(this.options).map(([key, value], i) => ({
          value: key,
          label: value,
          id: `checkbox-${key}-${i}`,
          disabled: false
        }))
      }

      return []
    }
  },
  methods: {
    isChecked(value) {
      if (Array.isArray(this.modelValue)) {
        return this.modelValue.includes(value)
      }
      return this.modelValue === value
    },

    toggleOption(value) {
      if (Array.isArray(this.modelValue)) {
        const newValue = [...this.modelValue]
        const index = newValue.indexOf(value)
        if (index > -1) {
          newValue.splice(index, 1)
        } else {
          newValue.push(value)
        }
        this.$emit('update:modelValue', newValue)
      } else {
        this.$emit('update:modelValue', value)
      }
    }
  }
}
</script>

<style lang="postcss" scoped>
.ews-checkbox-outer :deep(.ews-checkbox-element) {
  @apply mr-1;
}
.ews-checkbox-outer :deep(.ews-checkbox-wrapper) {
  @apply flex items-center;
}
.ews-checkbox-outer :deep(.checkbox-option) {
  @apply flex items-center mb-2;
}
.ews-checkbox-outer :deep(.checkbox-label) {
  @apply ml-2 font-light cursor-pointer;
}
.ews-checkbox-outer :deep(.ant-checkbox-wrapper:after) {
  display: none;
}
</style>
